import type { FC, ReactNode } from "react";
import { Navigate } from "react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import Stack from "@mui/material/Stack";
import {
  customerRegistrationFormQueryOptions,
  prefetchDpiaRecommendedForm,
  setConnectedTenantsMutationOptions,
  useGetRegistrationById,
  useSetResponsible,
} from "../api/registrations";
import { processorOf } from "../api/registrations.types";
import { DynamicForm } from "../dynamicForm";
import { DATE_OF_NEXT_AUDIT_TAG } from "../dynamicForm/types";
import { DynamicGrid } from "../dynamicGrid";
import { useCurrentCustomer } from "../root/CurrentOrganisationProvider";
import { DatesBand } from "../shared/DateTimeInfo";
import { Drawer } from "../shared/Drawer";
import { DpiaIcon1 } from "../shared/icons";
import { usePrivacyNavigate } from "../shared/usePrivacyNavigate";
import { useRegistrationVariant } from "../pages/RegistrationsGridPage/RegistrationVariantProvider";
import { ConnectedProcessor } from "../pages/RegistrationsGridPage/RegistrationDrawer/ConnectedProcessor";
import { ConnectedTenants } from "../pages/RegistrationsGridPage/RegistrationDrawer/ConnectedTenants";
import { DpiaRecommendedSubDrawerTitle } from "../pages/RegistrationsGridPage/RegistrationDrawer/DpiaRecommendedSubDrawer";
import { RegistrationDrawerHeader } from "../pages/RegistrationsGridPage/RegistrationDrawer/RegistrationDrawerHeader";
import { RegistrationResponsible } from "../pages/RegistrationsGridPage/RegistrationDrawer/RegistrationResponsible";
import {
  useHasPermissionToChooseResponsible,
  useHasPermissionToUpdateMetaData,
} from "../pages/RegistrationsGridPage/RegistrationDrawer/useHasPermissionToUpdate";
import { SharedProcessingAlert } from "./SharedProcessingAlert";

export const OrCustomerManaged: FC<{ ordinary: ReactNode; customerManaged: ReactNode }> = ({
  ordinary,
  customerManaged,
}) => {
  const registrationId = DynamicGrid.useSelectedRowId();
  const variant = useRegistrationVariant();
  const registrationByIdQuery = useGetRegistrationById(registrationId, variant);

  return registrationByIdQuery.data?.organisationEditability?.isCustomerManaged ? customerManaged : ordinary;
};

export const RedirectToOverview = () => {
  const registrationId = DynamicGrid.useSelectedRowId();
  const variant = useRegistrationVariant();
  const { paths } = usePrivacyNavigate();

  return <Navigate to={paths.registrationsInline({ id: registrationId, variant })} replace />;
};

export const CustomerManagedRegistrationDrawer = () => {
  const registrationId = DynamicGrid.useSelectedRowId();
  const { navigate } = usePrivacyNavigate();
  const variant = useRegistrationVariant();
  const currentCustomer = useCurrentCustomer();
  const registrationByIdQuery = useGetRegistrationById(registrationId, variant);
  const hasPermissionToUpdateMetaData = useHasPermissionToUpdateMetaData();
  const hasPermissionToChooseResponsible = useHasPermissionToChooseResponsible();
  const setResponsibleMutation = useSetResponsible(registrationId);
  const setConnectedTenantsMutation = useMutation(setConnectedTenantsMutationOptions(variant));
  const customerFormQuery = useQuery(customerRegistrationFormQueryOptions());

  return (
    <DynamicGrid.Drawer>
      {(() => {
        if (!registrationByIdQuery.data || !customerFormQuery.data) return <Drawer.Loading />;

        const registration = registrationByIdQuery.data;
        return (
          <DynamicForm.Provider
            key={registration.id}
            answers={registration.answers}
            isReadOnly
            disableQuestionsByTag={{ [DATE_OF_NEXT_AUDIT_TAG]: registration.status.status === "audit" }}
          >
            {() => (
              <>
                <RegistrationDrawerHeader
                  activeTab="standard"
                  registration={registration}
                  bottomSlot={<DatesBand createdAt={registration.createdAt} updatedAt={registration.updatedAt} />}
                />
                <Drawer.Content sx={{ pt: 3 }}>
                  <SharedProcessingAlert registration={registration} sx={{ mb: 3 }} />
                  <Stack sx={{ mb: 5 }}>
                    {variant === "Processor" && currentCustomer.customerKind !== "enterprise" && (
                      <ConnectedProcessor name={processorOf(registration)?.name} />
                    )}
                    <ConnectedTenants
                      isReadOnly={!hasPermissionToUpdateMetaData(registration)}
                      registration={registration}
                      setConnectedTenantsMutation={setConnectedTenantsMutation}
                    />
                    <RegistrationResponsible
                      isReadOnly={!hasPermissionToChooseResponsible(registration)}
                      registration={registration}
                      setResponsibleMutation={setResponsibleMutation}
                    />
                    <Drawer.GoToSubDrawerButtonsWrapper>
                      {variant === "Controller" && (
                        <Drawer.GoToSubDrawerButton
                          Icon={DpiaIcon1}
                          label={
                            <DpiaRecommendedSubDrawerTitle
                              sx={theme => ({ fontSize: theme.typography.pxToRem(14) })}
                              autoDPIAPreAssessment={registration.autoDPIAPreAssessment}
                            />
                          }
                          onMouseOver={() => prefetchDpiaRecommendedForm(variant)}
                          onClick={() => navigate("dpia-recommended")}
                        />
                      )}
                    </Drawer.GoToSubDrawerButtonsWrapper>
                  </Stack>

                  <Drawer.Divider />
                  <Stack sx={{ my: 5 }}>
                    <DynamicForm.InlineForm id={registration.id} form={customerFormQuery.data} />
                  </Stack>
                </Drawer.Content>
              </>
            )}
          </DynamicForm.Provider>
        );
      })()}
    </DynamicGrid.Drawer>
  );
};
